/**
 * RationalePanel
 *
 * A slide-over drawer ("Why" panel) that shows the full rationale for a
 * decision record. Opens from any Priority or Decision record to surface
 * the four-part rationale, health status, review date, and related chain.
 */

import { Clock, ChevronRight, AlertTriangle, Handshake } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SlideOverPanelStandalone } from '../panels/SlideOverPanel';
import { HEALTH_CONFIG } from '../../lib/decisionHealth';
import ConditionsPanel from './conditions/ConditionsPanel';
import { useConditionsEnabledForWorkspace } from '../../hooks/useConditionsEnabled';
import { buildCommitmentPrefillFromDecision } from '../../lib/commitmentPrefill';
import type { Decision } from '../../types/prioritization/decisions';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RationalePanelProps {
  open: boolean;
  onClose: () => void;
  decision: Decision | null;
  /** Optional: the item pool (focus list) this decision relates to. */
  itemPoolName?: string | null;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(isoString: string): string {
  try {
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoString;
  }
}

function isDatePast(isoString: string): boolean {
  return new Date(isoString) < new Date();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-2">
      {children}
    </h3>
  );
}

function RationaleField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  if (!value?.trim()) return null;
  return (
    <div>
      <dt className="text-xs font-medium text-[var(--text-ter)] mb-1">{label}</dt>
      <dd className="text-sm text-[var(--text-pri)] leading-relaxed whitespace-pre-wrap">
        {value}
      </dd>
    </div>
  );
}

function ImpactBadge({ impact }: { impact: Decision['impact'] }) {
  const styles: Record<Decision['impact'], string> = {
    high:   'bg-[var(--coral-dim)] text-[var(--coral)]',
    medium: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    low:    'bg-[var(--hover-tint)] text-[var(--text-sec)]',
  };
  const labels: Record<Decision['impact'], string> = {
    high:   'High Impact',
    medium: 'Medium Impact',
    low:    'Low Impact',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[impact]}`}>
      {labels[impact]}
    </span>
  );
}

function StatusBadge({ status }: { status: Decision['status'] }) {
  const styles: Record<Decision['status'], string> = {
    active:     'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    superseded: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    reversed:   'bg-[var(--coral-dim)] text-[var(--coral)]',
  };
  const labels: Record<Decision['status'], string> = {
    active:     'Active',
    superseded: 'Superseded',
    reversed:   'Reversed',
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}>
      {labels[status]}
    </span>
  );
}

function HealthBadge({ healthStatus }: { healthStatus: Decision['healthStatus'] }) {
  if (!healthStatus) return null;
  const config = HEALTH_CONFIG[healthStatus];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${config.color}`}>
      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${config.dot}`} />
      {config.label}
    </span>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function RationalePanel({
  open,
  onClose,
  decision,
  itemPoolName,
}: RationalePanelProps) {
  // Record-scoped gate: this drawer opens over a decision reached by URL, which
  // can belong to a different workspace than the session's (the 2026-08-21
  // moduleGate defect note).
  const conditionsEnabled = useConditionsEnabledForWorkspace(decision?.workspaceId);
  const navigate = useNavigate();

  const handleCreateCommitment = () => {
    if (!decision) return;
    const commitmentPrefill = buildCommitmentPrefillFromDecision({
      id: decision.id,
      title: decision.title,
      itemId: decision.itemId,
    });
    onClose();
    navigate('/prioritization/commitments', { state: { commitmentPrefill } });
  };

  const hasAnyRationale =
    !!decision?.rationaleContext?.trim() ||
    !!decision?.rationaleReasoning?.trim() ||
    !!decision?.rationaleAlternatives?.trim() ||
    !!decision?.reconsiderationConditions?.trim();

  const footer = (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={onClose}
        className="bg-[var(--bg-raised)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] border border-[var(--rule)] hover:bg-[var(--surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        Close
      </button>
    </div>
  );

  return (
    <SlideOverPanelStandalone
      open={open}
      onClose={onClose}
      title="Decision Rationale"
      size="lg"
      footer={footer}
    >
      {!decision ? (
        <div className="flex items-center justify-center h-32">
          <p className="text-sm text-[var(--text-ter)] italic">No decision selected.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* ── Header area ─────────────────────────────────────────────── */}
          <div className="space-y-2">
            <h2 className="text-base font-semibold text-[var(--text-pri)] leading-snug">
              {decision.title}
            </h2>
            <div className="flex flex-wrap items-center gap-2">
              <ImpactBadge impact={decision.impact} />
              {decision.healthStatus && (
                <HealthBadge healthStatus={decision.healthStatus} />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--text-ter)]">
              {decision.decidedAt && (
                <span>Decided {formatDate(decision.decidedAt)}</span>
              )}
              {decision.decidedByName && (
                <span>by {decision.decidedByName}</span>
              )}
            </div>
            <button
              type="button"
              onClick={handleCreateCommitment}
              className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] hover:text-[var(--text-ter)] hover:border-[var(--slate)] transition-colors"
            >
              <Handshake className="h-3.5 w-3.5" />
              Create a commitment from this decision
            </button>
          </div>

          {/* ── Rationale Chain ──────────────────────────────────────────── */}
          {itemPoolName && (
            <div>
              <SectionHeading>Rationale Chain</SectionHeading>
              <div className="flex items-center gap-1.5 text-xs text-[var(--text-ter)] bg-[var(--surface)] border border-[var(--rule)] px-3 py-2">
                <span className="font-medium text-[var(--text-sec)]">
                  Focus List: {itemPoolName}
                </span>
                <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                <span className="text-[var(--text-ter)]">this decision</span>
              </div>
            </div>
          )}

          {/* ── Four rationale fields ─────────────────────────────────────── */}
          <div>
            <SectionHeading>Rationale</SectionHeading>
            {hasAnyRationale ? (
              <dl className="space-y-4">
                <RationaleField
                  label="Context"
                  value={decision.rationaleContext}
                />
                <RationaleField
                  label="Reasoning"
                  value={decision.rationaleReasoning}
                />
                <RationaleField
                  label="Alternatives Considered"
                  value={decision.rationaleAlternatives}
                />
                <RationaleField
                  label="Reconsideration Conditions"
                  value={decision.reconsiderationConditions}
                />
              </dl>
            ) : (
              /* Orphaned Decision callout */
              <div className="flex gap-3 border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3">
                <AlertTriangle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[var(--mustard)]">
                    Orphaned Decision
                  </p>
                  <p className="mt-0.5 text-sm text-[var(--mustard)]">
                    No rationale has been documented for this decision. Consider
                    editing this record to add context.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ── Typed conditions (C1) ───────────────────────────────────── */}
          {conditionsEnabled && (
            <ConditionsPanel
              subject={{ kind: 'decision', decisionId: decision.id }}
              subjectLabel={decision.title}
              existingFreeText={decision.reconsiderationConditions}
              workspaceId={decision.workspaceId}
            />
          )}

          {/* ── Review By ────────────────────────────────────────────────── */}
          {decision.reviewByDate && (
            <div>
              <SectionHeading>Review By</SectionHeading>
              <div className="flex items-center gap-2 text-sm">
                <Clock
                  className={`h-4 w-4 flex-shrink-0 ${
                    isDatePast(decision.reviewByDate)
                      ? 'text-[var(--coral)]'
                      : 'text-[var(--text-ter)]'
                  }`}
                />
                <span
                  className={
                    isDatePast(decision.reviewByDate)
                      ? 'text-[var(--coral)] font-medium'
                      : 'text-[var(--text-sec)]'
                  }
                >
                  {formatDate(decision.reviewByDate)}
                </span>
                {isDatePast(decision.reviewByDate) && (
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-[var(--coral-dim)] text-[var(--coral)]">
                    Overdue
                  </span>
                )}
              </div>
            </div>
          )}

          {/* ── Status ───────────────────────────────────────────────────── */}
          <div>
            <SectionHeading>Status</SectionHeading>
            <StatusBadge status={decision.status} />
          </div>

          {/* ── Notes (body) ──────────────────────────────────────────────── */}
          {decision.body?.trim() && (
            <div>
              <SectionHeading>Notes</SectionHeading>
              <p className="text-sm text-[var(--text-pri)] leading-relaxed whitespace-pre-wrap">
                {decision.body}
              </p>
            </div>
          )}
        </div>
      )}
    </SlideOverPanelStandalone>
  );
}

export default RationalePanel;
